(function ($, Drupal, once) {

  /**
   * Table of Contents Generator Class
   * 
   * Finds all h2 and h3 elements with class 'vsc-heading' and creates
   * a table of contents menu with anchor links. Uses Intersection Observer
   * to highlight the active link when the heading is in view.
   */
  class TableOfContents {

    constructor(element) {
      this.container = element;
      this.headings = [];
      this.tocNav = null;
      this.observer = null;
      this.activeLink = null;

      // Accordion properties
      this.accordionTrigger = null;
      this.accordionContent = null;
      this.accordionClickHandler = null;
      this.accordionKeyHandler = null;
      this.accordionInitialized = false;
      this.desktopMediaQuery = null;
      this.resizeHandler = null;

      // Class names
      this.initialisedClass = 'vits-toc--initialised';
      this.accordionInitialisedClass = 'vits-toc--accordion-initialised';
      this.accordionOpenClass = 'vits-toc--accordion-open';
      this.navClass = 'vits-toc__nav';
      this.accordionContainerClass = 'vits-toc__accordion-container';
      this.accordionTriggerClass = 'vits-toc__accordion-trigger';
      this.accordionTriggerHeadingClass = 'vits-toc__heading';
      this.accordionContentClass = 'vits-toc__accordion-content';
      this.listClass = 'vits-toc__list';
      this.itemClass = 'vits-toc__item';
      this.linkClass = 'vits-toc__link';
      this.activeLinkClass = 'vits-toc__link--active';

      this.init();
    }
    
    /**
     * Initialize the table of contents
     */
    init() {
      this.findHeadings();

      if (this.headings.length === 0) {
        return;
      }

      this.assignHeadingIds();
      this.createTocStructure();
      this.insertTocIntoPage();
      this.setupIntersectionObserver();
      this.setupResponsiveAccordion();

      // Add initialised class to container
      this.container.classList.add(this.initialisedClass);
    }
    
    /**
     * Find all h2 and h3 elements with class 'vsc-heading'
     */
    findHeadings() {
      this.headings = Array.from(
        document.querySelectorAll('.vsc-heading h2, .vsc-heading h3')
      );
    }
    
    /**
     * Assign unique IDs to each heading based on heading text
     */
    assignHeadingIds() {
      const usedIds = new Set();

      this.headings.forEach((heading) => {
        // Get the heading text and convert to slug
        const headingText = heading.textContent.trim();
        let slug = headingText
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
          .replace(/\s+/g, '-')          // Replace spaces with hyphens
          .replace(/-+/g, '-')           // Replace multiple hyphens with single
          .replace(/^-|-$/g, '');        // Remove leading/trailing hyphens

        // Ensure uniqueness
        let headingId = slug;
        let counter = 1;
        while (usedIds.has(headingId)) {
          headingId = `${slug}-${counter}`;
          counter++;
        }

        usedIds.add(headingId);
        heading.id = headingId;
      });
    }
    
    /**
     * Create the TOC navigation structure
     */
    createTocStructure() {
      // Create nav container
      this.tocNav = document.createElement('nav');
      this.tocNav.className = this.navClass;
      this.tocNav.setAttribute('aria-label', 'Table of Contents');

      // Create definition list wrapper
      const tocDl = document.createElement('dl');
      tocDl.className = this.accordionContainerClass;

      // Create dt (heading/trigger)
      const tocDt = document.createElement('dt');
      tocDt.className = this.accordionTriggerClass;

      const tocHeading = document.createElement('h2');
      tocHeading.className = this.accordionTriggerHeadingClass;
      tocHeading.textContent = 'Jump to...';

      tocDt.appendChild(tocHeading);

      // Create dd (content)
      const tocDd = document.createElement('dd');
      tocDd.className = this.accordionContentClass;

      // Create list inside dd
      const tocList = document.createElement('ul');
      tocList.className = this.listClass;

      // Create items for each heading
      this.headings.forEach((heading) => {
        const tocItem = this.createTocItem(heading);
        tocList.appendChild(tocItem);
      });

      // Assemble structure
      tocDd.appendChild(tocList);
      tocDl.appendChild(tocDt);
      tocDl.appendChild(tocDd);
      this.tocNav.appendChild(tocDl);
    }
    
    /**
     * Create a single TOC list item
     *
     * @param {HTMLElement} heading - The heading element
     * @returns {HTMLElement} The list item element
     */
    createTocItem(heading) {
      const headingId = heading.id;

      // Create list item
      const tocItem = document.createElement('li');
      tocItem.className = this.itemClass;
      
      // Create link
      const tocLink = document.createElement('a');
      tocLink.className = this.linkClass;
      tocLink.href = '#' + headingId;
      tocLink.textContent = heading.textContent;
      tocLink.setAttribute('data-heading-id', headingId);
      
      // Add click handler
      tocLink.addEventListener('click', (e) => {
        this.handleLinkClick(e, headingId);
      });
      
      // Append link to item
      tocItem.appendChild(tocLink);
      
      return tocItem;
    }
    
    /**
     * Handle click on TOC link
     *
     * @param {Event} e - The click event
     * @param {string} headingId - The target heading ID
     */
    handleLinkClick(e, headingId) {
      e.preventDefault();

      const targetHeading = document.getElementById(headingId);
      if (!targetHeading) {
        return;
      }

      // Get navbar offset from CSS custom property
      const navbarContentOffset = parseFloat(
        window.getComputedStyle(document.documentElement)
          .getPropertyValue('--navbar-content-offset')
          .trim()
      );

      // Set base offset (defaults to 0 if navbar offset not found)
      let offset = navbarContentOffset || 0;

      // Add extra padding for visual breathing room
      const extraPadding = 16;
      offset += extraPadding;

      // Get the element's current position relative to the viewport
      const rect = targetHeading.getBoundingClientRect();

      // Get the absolute scroll position we need to reach
      const targetPosition = rect.top + window.scrollY - offset;

      // Smooth scroll to target
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });

      history.pushState(null, null, '#' + headingId);
    }
    
    /**
     * Insert TOC into the page
     */
    insertTocIntoPage() {
      // Insert TOC into the container element
      this.container.appendChild(this.tocNav);
    }

    /**
     * Set up Intersection Observer for active state tracking
     */
    setupIntersectionObserver() {
      const observerOptions = {
        root: null,
        rootMargin: '16px 0px -70% 0px',
        threshold: 0
      };

      this.observer = new IntersectionObserver(
        (entries) => this.handleIntersection(entries),
        observerOptions
      );

      // Observe all headings
      this.headings.forEach((heading) => {
        this.observer.observe(heading);
      });
    }

    /**
     * Handle intersection observer callback
     *
     * @param {IntersectionObserverEntry[]} entries - The intersection entries
     */
    handleIntersection(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const headingId = entry.target.id;
          this.setActiveLink(headingId);
        }
      });
    }

    /**
     * Set the active link based on heading ID
     *
     * @param {string} headingId - The heading ID to set as active
     */
    setActiveLink(headingId) {
      const correspondingLink = this.tocNav.querySelector(
        '[data-heading-id="' + headingId + '"]'
      );

      if (!correspondingLink) {
        return;
      }

      // Remove active class from previous link
      if (this.activeLink) {
        this.activeLink.classList.remove(this.activeLinkClass);
        this.activeLink.removeAttribute('aria-current');
      }

      // Add active class to current link
      correspondingLink.classList.add(this.activeLinkClass);
      correspondingLink.setAttribute('aria-current', 'true');
      this.activeLink = correspondingLink;
    }

    /**
     * Set up accordion functionality for mobile
     */
    setupAccordion() {
      const trigger = this.tocNav.querySelector('.' + this.accordionTriggerClass);
      const content = this.tocNav.querySelector('.' + this.accordionContentClass);

      if (!trigger || !content) {
        return;
      }

      // Store references
      this.accordionTrigger = trigger;
      this.accordionContent = content;

      // Add ARIA attributes
      trigger.setAttribute('role', 'button');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', 'vits-toc-content');
      trigger.setAttribute('tabindex', '0');

      content.setAttribute('id', 'vits-toc-content');

      // Add click handler
      this.accordionClickHandler = () => {
        $(this.accordionContent).slideToggle(300);
        this.container.classList.toggle(this.accordionOpenClass);

        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', !isExpanded);
      };

      trigger.addEventListener('click', this.accordionClickHandler);

      // Add keyboard handler (Enter and Space)
      this.accordionKeyHandler = (e) => {
        const keyCode = e.keyCode || e.which;
        if (keyCode === 13 || keyCode === 32) {
          e.preventDefault();
          trigger.click();
        }
      };

      trigger.addEventListener('keydown', this.accordionKeyHandler);

      // Add accordion initialised class to container
      this.container.classList.add(this.accordionInitialisedClass);
    }

    /**
     * Destroy accordion functionality
     */
    destroyAccordion() {
      if (!this.accordionInitialized) {
        return;
      }

      const trigger = this.accordionTrigger;
      const content = this.accordionContent;

      // Remove event listeners
      if (this.accordionClickHandler) {
        trigger.removeEventListener('click', this.accordionClickHandler);
      }

      if (this.accordionKeyHandler) {
        trigger.removeEventListener('keydown', this.accordionKeyHandler);
      }

      // Remove ARIA attributes
      trigger.removeAttribute('role');
      trigger.removeAttribute('aria-expanded');
      trigger.removeAttribute('aria-controls');
      trigger.removeAttribute('tabindex');

      content.removeAttribute('id');

      // Remove classes
      this.container.classList.remove(this.accordionOpenClass);

      // Show content (remove inline display:none from slideToggle)
      $(content).css('display', '');

      this.accordionInitialized = false;

      // Remove accordion initialised class to container
      this.container.classList.remove(this.accordionInitialisedClass);
    }

    /**
     * Set up responsive accordion handler
     */
    setupResponsiveAccordion() {
      // Create media query for Desktop large breakpoint (1200px)
      this.desktopMediaQuery = window.matchMedia('(min-width: 1200px)');

      // Handler function
      this.accordionResponsiveHandler = () => {
        if (this.desktopMediaQuery.matches) {
          // Desktop: Destroy accordion
          this.destroyAccordion();
        } else {
          // Mobile: Initialize accordion
          if (!this.accordionInitialized) {
            this.setupAccordion();
            this.accordionInitialized = true;
          }
        }
      };

      // Run on init
      this.accordionResponsiveHandler();

      // Add throttled resize listener
      this.resizeHandler = customLodash.throttle(this.accordionResponsiveHandler, 300);
      window.addEventListener('resize', this.resizeHandler);
    }

    /**
     * Destroy the table of contents and clean up
     */
    destroy() {
      // Disconnect observer
      if (this.observer) {
        this.observer.disconnect();
      }

      // Remove resize listener
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
      }

      // Destroy accordion
      this.destroyAccordion();

      // Remove TOC from DOM
      if (this.tocNav && this.tocNav.parentNode) {
        this.tocNav.parentNode.removeChild(this.tocNav);
      }

      // Remove IDs from headings
      this.headings.forEach((heading) => {
        heading.removeAttribute('id');
      });

      // Remove initialised class from container
      this.container.classList.remove(this.initialisedClass);
    }
  }

  /**
   * Drupal behavior for Table of Contents
   */
  Drupal.behaviors.vitsTableOfContents = {

    attach: function (context, settings) {

      // Initialize TOC for each .vits-toc container
      once('vitsTableOfContents', '.vits-toc', context).forEach(function (element) {
        new TableOfContents(element);
      });

    }
  };

})(jQuery, Drupal, once);

